import { getWindowObject } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';

export const USE_LOCAL_TD = false; // process.env.NODE_ENV !== 'production';

const TD_UI_VERSION =
    getWindowObject('__SERVER_DATA__')?.request?.params?.uiVersion;

const eventbriteHostname =
    getWindowObject('location')?.hostname || 'www.eventbrite.com';

const boostSubdomain = eventbriteHostname
    .replace('eb.toneden.dev', 'boost.eventbrite.dev:4000')
    .replace('eb.toneden.io', 'boost.eventbrite.com')
    .replace('www', 'boost')
    .replace('evbdev', 'eventbrite')
    .replace('evbqa', 'eventbrite')
    .replace('dev.', 'boost.');

export const TD_SERVER_URL = USE_LOCAL_TD
    ? 'https://boost.eventbrite.dev:4000'
    : `https://${boostSubdomain}`;
export const TD_API_URL = USE_LOCAL_TD
    ? 'https://boost.eventbrite.dev:4000/api/v1/'
    : `https://${boostSubdomain}/api/v1/`;
export const TD_CSS_URL = USE_LOCAL_TD
    ? 'https://www.toneden.dev:8081/stylesheets/build'
    : `https://st.toneden.io/${
          TD_UI_VERSION ? TD_UI_VERSION : 'production'
      }/stylesheets`;
export const TD_JS_URL = USE_LOCAL_TD
    ? 'https://www.toneden.dev:8081/javascripts/build'
    : `https://st.toneden.io/${
          TD_UI_VERSION ? TD_UI_VERSION : 'production'
      }/javascripts`;

export const slackActionItems = [
    {
        content: gettext('Change Channel'),
        value: 'switch_channel',
    },
    {
        content: gettext('Disconnect Slack'),
        value: 'disconnect',
    },
];

export const emailActionItems = [
    {
        content: gettext('Delete Email'),
        value: 'delete_email',
    },
];

export const notificationTypes = [
    {
        title: 'Daily Campaign Reports',
        description:
            'Daily messages that inform you on the performance of your advertising and playbook campaigns.',
        key: 'ad_stats_daily',
    },
    {
        title: `Weekly Campaign Report`,
        description: `Weekly messages that inform you on the performance of your advertising and playbook campaigns.`,
        key: 'ad_stats_weekly',
    },
    {
        title: `Monthly Campaign Report`,
        description: `Monthly messages that inform you on the performance of your advertising and playbook campaigns.`,
        key: 'ad_stats_monthly',
    },
    {
        title: `Final Campaign Report`,
        description: `A final message that shows the campaign's overall performance. Sent once your ad or playbook campaign ends.`,
        key: 'ad_stats_final',
    },
    {
        title: `First Conversion`,
        description: `A real-time message that’s sent when any of your ad or playbook campaigns sees its first conversion.`,
        key: 'ad_first_conversion',
    },
    {
        title: `Recommendations`,
        description: `A real-time message that includes recommendations to improve the performance of your ad or playbook campaigns.`,
        key: 'ad_recommendation',
    },
];

export const defaultChannelsByType = {
    ad_stats_daily: {
        email: true,
        slack: true,
    },
    ad_stats_final: {
        email: false,
        slack: true,
    },
    ad_stats_weekly: {
        email: true,
        slack: false,
    },
    ad_stats_monthly: {
        email: true,
        slack: false,
    },
    ad_recommendation: {
        email: true,
        slack: false,
    },
    predraft_campaign: {
        email: true,
        slack: true,
    },
    ad_first_conversion: {
        email: false,
        slack: true,
    },
};

const planStarter = [
    {
        prefix: '$100',
        info: gettext('Monthly ad spend limit'),
    },
    {
        prefix: '6000',
        info: gettext('Email campaigns daily limit'),
    },
    {
        prefix: null,
        info: gettext('Publish and sell on Facebook'),
    },
    {
        prefix: null,
        info: gettext('Instagram Growth ads'),
    },
    {
        prefix: null,
        info: gettext('Multi event ads'),
    },
    {
        prefix: null,
        info: gettext('Branded landing pages'),
    },
];
const paidPlanBase = [
    {
        prefix: '6000',
        info: gettext('Email campaigns daily limit'),
    },
    {
        prefix: null,
        info: gettext('Publish and sell on Facebook'),
    },
    {
        prefix: null,
        info: gettext('Facebook and Instagram ads'),
    },
    {
        prefix: null,
        info: gettext('Instagram Growth ads'),
    },
    {
        prefix: null,
        info: gettext('Multi event ads'),
    },
    {
        prefix: null,
        info: gettext('Branded landing pages'),
    },
    {
        prefix: null,
        info: gettext('Automated event email reminders'),
    },
    {
        prefix: null,
        info: gettext('Marketing insights and reports'),
    },
];

const planPlus = [
    {
        prefix: '$500',
        info: gettext('Monthly ad spend limit'),
    },
    ...paidPlanBase,
];
const planProOld = [
    {
        prefix: gettext('Unlimited'),
        info: gettext('monthly ad spend limit'),
    },
    ...paidPlanBase,
];
const planIncluded = [
    {
        prefix: '2000',
        info: gettext('Email campaigns daily limit'),
    },
    {
        prefix: null,
        info: gettext('Publish and sell on Facebook'),
    },
];
const planFree = [
    {
        prefix: null,
        info: gettext('Send email campaigns'),
    },
    {
        prefix: null,
        info: gettext('Publish and sell on Facebook'),
    },
    {
        prefix: null,
        info: gettext('Facebook and Instagram ads'),
    },
    {
        prefix: null,
        info: gettext('Instagram growth ads'),
    },
    {
        prefix: null,
        info: gettext('Multi event ads'),
    },
];

const planLite = [
    {
        prefix: null,
        info: gettext('Email Marketing (2,000 sends/day)'),
    },
    {
        prefix: null,
        info: gettext('Marketing Insights & Reports'),
    },
];

const planCore = [
    {
        prefix: null,
        info: gettext('Email Marketing (6,000 sends/day)'),
    },
    {
        prefix: null,
        info: gettext('Marketing Insights & Reports'),
    },
    {
        prefix: null,
        info: gettext('Facebook & Instagram Ads'),
    },
    {
        prefix: null,
        info: gettext('Smart Targeting'),
    },
];
const planPro = [
    {
        prefix: null,
        info: gettext('Email Marketing (10,000 sends/day)'),
    },
    {
        prefix: null,
        info: gettext('Marketing Insights & Reports'),
    },
    {
        prefix: null,
        info: gettext('Facebook & Instagram Ads'),
    },
    {
        prefix: null,
        info: gettext('Smart Targeting'),
    },
    {
        prefix: null,
        info: gettext('Automated Multi-Event Ads'),
    },
    {
        prefix: null,
        info: gettext('Instagram Story Ads'),
    },
];
const planTrial = [
    {
        prefix: null,
        info: gettext('Marketing Insights & Reports'),
    },
    {
        prefix: null,
        info: gettext('Facebook & Instagram Ads'),
    },
    {
        prefix: null,
        info: gettext('Smart Targeting'),
    },
];

type IncludeInfo = {
    [key: string]: Array<object>;
};
export const planIncludeInfo: IncludeInfo = {
    Included: planIncluded,
    Free: planFree,
    Starter: planStarter,
    Plus: planPlus,
    ProOld: planProOld,
    Lite: planLite,
    Core: planCore,
    Pro: planPro,
    Trial: planTrial,
};

export const TITLE_FACEBOOK_LOGOUT = gettext(
    'Your Facebook account is connected to your active campaigns',
);
export const FACEBOOK_LOGOUT_DESCRIPTION = gettext(
    "If you sign out of Facebook, you won't be able to monitor your existing campaigns and events on Facebook and Instagram.",
);
export const CANCEL_LOGOUT = gettext('Cancel');
export const DO_LOGOUT = gettext('Disconnect Facebook account');

export const LOADING_PAGES = gettext('Loading pages...');
export const NO_LIVE_PAGES = gettext("You don't have any live pages");
export const ONLY_PUBLISHED_PAGES = gettext(
    'Only published pages will display here',
);

// STATSIG EVENT NAMES
export const MARKETING_BILLING_MODAL_CLICK_UPGRADE_PLAN =
    'Marketing - Billing Modal - Click - Upgrade Plan';
